"use client";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import type { MenusOutput } from "@/interfaces/menus";
import { RichTextEditor } from "@/components/RichTextEditor";
import CurrencyInput from "@/components/CurrencyInput";
import {
  Store,
  DollarSign,
  Truck,
  MapPin,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MenuGeneralSettingsProps {
  menu: MenusOutput;
  onMenuChange: (menu: MenusOutput) => void;
}

export function MenuGeneralSettings({
  menu,
  onMenuChange,
}: MenuGeneralSettingsProps) {
  const RequiredField = () => (
    <span className="text-red-500 ml-1" title="Campo obrigatório">
      *
    </span>
  );

  const FieldInfo = ({ text }: { text: string }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <AlertCircle className="h-4 w-4 text-muted-foreground ml-1 inline cursor-help" />
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="space-y-8">
      {/* Seção de Informações Básicas */}
      <div className="bg-slate-50 p-5 rounded-lg border">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b">
          <Store className="h-5 w-5 text-slate-600" />
          <h3 className="text-md font-medium">Informações Básicas</h3>
        </div>

        <div className="grid gap-6 max-w-3xl">
          <div>
            <Label htmlFor="menu-name" className="flex items-center">
              Nome do Catálogo <RequiredField />
              <FieldInfo text="Nome que será exibido para os clientes" />
            </Label>
            <Input
              id="menu-name"
              value={menu.name}
              required
              maxLength={50}
              placeholder="Ex: Catálogo Principal"
              className="max-w-md"
              onChange={(e) =>
                onMenuChange({
                  ...menu,
                  name: e.target.value,
                })
              }
            />
          </div>

          <div>
            <Label htmlFor="menu-description" className="flex items-center">
              Descrição do Catálogo
              <FieldInfo text="Descreva brevemente o seu catálogo" />
            </Label>
            <RichTextEditor
              value={menu.description}
              onChange={(value) =>
                onMenuChange({
                  ...menu,
                  description: value,
                })
              }
              placeholder="Descreva o catálogo..."
            />
          </div>

          <div>
            <Label htmlFor="minimum-order" className="flex items-center">
              <DollarSign className="h-4 w-4 mr-1" />
              Valor Mínimo do Pedido
              <FieldInfo text="Valor mínimo para aceitar um pedido" />
            </Label>
            <CurrencyInput
              id="minimum-order"
              value={menu.minOrderPrice}
              onValueChange={(value) =>
                onMenuChange({
                  ...menu,
                  minOrderPrice: value,
                })
              }
              className="max-w-[130px]"
            />
          </div>
        </div>
      </div>

      {/* Seção de Configurações de Entrega */}
      <div className="bg-slate-50 p-5 rounded-lg border">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b">
          <Truck className="h-5 w-5 text-slate-600" />
          <h3 className="text-md font-medium">Configurações de Entrega</h3>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Switch
            id="accept-delivery"
            checked={menu.acceptDelivery}
            onCheckedChange={(checked) =>
              onMenuChange({ ...menu, acceptDelivery: checked })
            }
          />
          <Label htmlFor="accept-delivery" className="cursor-pointer">
            Aceitar entregas
          </Label>
        </div>
        {menu.acceptDelivery && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <Label htmlFor="minimum-delivery-fee" className="flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                Taxa de Entrega Mínima
              </Label>
            <CurrencyInput
              id="minimum-delivery-fee"
              value={menu.minDeliveryPrice}
              onValueChange={(value) =>
                onMenuChange({
                  ...menu,
                  minDeliveryPrice: value,
                })
              }
            />
          </div>

          <div>
            <Label htmlFor="maximum-delivery-fee" className="flex items-center">
              <DollarSign className="h-4 w-4 mr-1" />
              Taxa de Entrega Máxima
            </Label>
            <CurrencyInput
              id="maximum-delivery-fee"
              value={menu.maxDeliveryPrice || 0}
              onValueChange={(value) =>
                onMenuChange({
                  ...menu,
                  maxDeliveryPrice: value,
                })
              }
            />
          </div>

          <div>
            <Label htmlFor="delivery-fee-per-km" className="flex items-center">
              <DollarSign className="h-4 w-4 mr-1" />
              Taxa por KM
              <FieldInfo text="Valor cobrado por quilômetro de distância" />
            </Label>
            <CurrencyInput
              id="delivery-fee-per-km"
              value={menu.kmTax}
              onValueChange={(value) =>
                onMenuChange({
                  ...menu,
                  kmTax: value,
                })
              }
            />
          </div>

          <div>
            <Label htmlFor="max-distance" className="flex items-center">
              <MapPin className="h-4 w-4 mr-1" />
              Distância Máxima (KM)
              <FieldInfo text="Distância máxima para realizar entregas" />
            </Label>
            <Input
              id="max-distance"
              type="number"
              min="0"
              step="0.1"
              value={menu.maxDistance}
              className="max-w-[100px]"
              onChange={(e) =>
                onMenuChange({
                  ...menu,
                  maxDistance: Number.parseFloat(e.target.value) || 0,
                })
              }
            />
          </div>

          <div>
            <Label htmlFor="min-delivery-time" className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Tempo Mínimo (min)
              <FieldInfo text="Tempo mínimo estimado para entrega" />
            </Label>
            <Input
              id="min-delivery-time"
              type="number"
              min="0"
              value={menu.minDeliveryTime}
              className="max-w-[100px]"
              onChange={(e) =>
                onMenuChange({
                  ...menu,
                  minDeliveryTime: Number.parseInt(e.target.value) || 0,
                })
              }
            />
          </div>

          <div>
            <Label htmlFor="max-delivery-time" className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Tempo Máximo (min)
              <FieldInfo text="Tempo máximo estimado para entrega" />
            </Label>
            <Input
              id="max-delivery-time"
              type="number"
              min="0"
              value={menu.maxDeliveryTime}
              className="max-w-[100px]"
              onChange={(e) =>
                onMenuChange({
                  ...menu,
                  maxDeliveryTime: Number.parseInt(e.target.value) || 0,
                })
              }
            />
          </div>
          </div>
        )}
      </div>
    </div>
  );
}
